// FROM: https://github.com/modusign/modusign-client/blob/35ac05fa1f9337ff1aeacd765766a3b36653b583/app/src/components/CheckboxField/CheckboxFieldContextMenu/index.tsx

import { Box, Flex, Option, RadioButton, Select, Text } from '@modusign/design-system';
import { FormEvent, MouseEvent } from 'react';
import { useSelector } from 'react-redux';

import { OnOffButton } from '@app/components/Buttons';
import { ContextMenuRowDivider, ContextMenuWrapper } from '@app/components/ContextMenu';
import ContextMenuRowParticipantName from '@app/components/ContextMenuRowParticipantName';
import DataLabelInput from '@app/components/DataLabelInput';
import EnhancedToolTip from '@app/components/EnhancedToolTip';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import CheckboxGroupInputMode from '@app/store/CheckboxGroupInputMode';
import getIsDataLabelEditable from '@app/store/selectors/getIsDataLabelEditable';

const MAXIMUM_CHECKBOX_COUNT = 20;

export interface CheckboxGroupContextMenuProps {
  checkboxCount: number;
  dataLabel: string;
  dataLabelErrorMessage: string;
  required: boolean;
  restrictModification?: boolean;
  inputMode: CheckboxGroupInputMode;
  participantName?: string;
  participantOrder: number;
  onDataLabelChange: (value: string) => void;
  onRequiredChange: (required: boolean) => void;
  onCheckboxCountSelect: (value: number) => void;
  onInputModeChange: (type: CheckboxGroupInputMode) => void;
}

function CheckboxGroupContextMenu({
  checkboxCount,
  dataLabel,
  dataLabelErrorMessage,
  required,
  restrictModification,
  inputMode,
  participantName,
  participantOrder,
  onCheckboxCountSelect,
  onDataLabelChange,
  onRequiredChange,
  onInputModeChange,
}: CheckboxGroupContextMenuProps) {
  const isDataLabelEditable = useSelector(getIsDataLabelEditable);

  function handleRequiredChange(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    onRequiredChange(!required);
  }
  function handleCheckboxCountChange(event: FormEvent<HTMLSelectElement>) {
    const value = Number(event.currentTarget.value);
    onCheckboxCountSelect(value);
    pushAdobeDataLayer({
      event: 'changeCheckboxCountSelect',
      checkboxCount: value,
    });
  }

  function handleCheckboxCountClick() {
    pushAdobeDataLayer({ event: 'clickCheckboxCountSelect' });
  }

  const isSingleCheckbox = checkboxCount < 2;

  return (
    <ContextMenuWrapper>
      {participantName && (
        <>
          <ContextMenuRowParticipantName
            participantName={participantName}
            participantOrder={participantOrder}
          />
          <ContextMenuRowDivider />
        </>
      )}
      <Flex alignItems="center" justifyContent="space-between" marginY="15px">
        <Text fontWeight="regular">필수 선택</Text>
        <OnOffButton active={required} aria-label="필수 선택 여부" onClick={handleRequiredChange} />
      </Flex>
      <ContextMenuRowDivider />
      <Flex alignItems="center" justifyContent="space-between" marginY="12px">
        <Text fontWeight="regular">항목 개수</Text>
        <Box justifyContent="flex-end">
          <Select
            aria-label="체크박스 개수 선택하기"
            backgroundColor="white"
            disabled={restrictModification}
            value={checkboxCount}
            width="84px"
            onChange={handleCheckboxCountChange}
            onClick={handleCheckboxCountClick}
          >
            {Array.from({ length: MAXIMUM_CHECKBOX_COUNT }, (_, i) => i + 1).map((i) => (
              <Option key={i} value={i}>{`${i}개`}</Option>
            ))}
          </Select>
        </Box>
      </Flex>
      {checkboxCount > 1 && (
        <>
          <ContextMenuRowDivider />
          <Box marginTop="12px" textAlign="left" width="100%">
            <Text fontWeight="regular">
              선택 개수
              <Box
                as="i"
                className="fa fa-question-circle "
                color="darkGray100"
                data-testid="checkbox-group-selection-count-help-icon"
                paddingLeft={1}
                sx={{
                  '&:hover': {
                    '& .CheckboxGroupSelectHelp__tooltip': {
                      display: 'inline-block',
                    },
                  },
                }}
              >
                <Box
                  className="CheckboxGroupSelectHelp__tooltip"
                  display="none"
                  marginTop="6px"
                  position="fixed"
                  width="305px"
                >
                  <EnhancedToolTip arrowDirection="left" style={{ paddingBottom: '10px' }}>
                    서명자가 선택할 수 있는 항목 개수를 설정합니다.
                  </EnhancedToolTip>
                </Box>
              </Box>
            </Text>
          </Box>
          <Flex alignItems="center" marginBottom="12px" marginTop="6px">
            <Flex alignItems="center" as="label" sx={{ cursor: 'pointer' }}>
              <RadioButton
                aria-label="하나만 선택"
                checked={!isSingleCheckbox && inputMode === 'RADIO'}
                disabled={isSingleCheckbox}
                onClick={() => onInputModeChange('RADIO')}
              />
              <Text paddingLeft="6px"> 하나만</Text>
            </Flex>
            <Flex alignItems="center" as="label" marginLeft="25px" sx={{ cursor: 'pointer' }}>
              <RadioButton
                aria-label="여러개 선택"
                checked={!isSingleCheckbox && inputMode === 'CHECKBOX'}
                disabled={isSingleCheckbox}
                onClick={() => onInputModeChange('CHECKBOX')}
              />
              <Text paddingLeft="6px"> 여러개</Text>
            </Flex>
          </Flex>
        </>
      )}
      {isDataLabelEditable && (
        <>
          <ContextMenuRowDivider />
          <Box pb="20px" pt="10px" textAlign="left">
            <DataLabelInput
              errorMessage={dataLabelErrorMessage}
              value={dataLabel}
              onChange={onDataLabelChange}
            />
          </Box>
        </>
      )}
    </ContextMenuWrapper>
  );
}

export default CheckboxGroupContextMenu;
